import { z } from 'zod'

// API Base URL
const API_BASE_URL = 'https://sui-explorer-api-production.space-and-time.workers.dev/api'
// const API_BASE_URL = "http://localhost:8787/api"


// API Error Schema
export const ApiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.any().optional(),
})
export type ApiError = z.infer<typeof ApiErrorSchema>

const marketData = z.object({
	currentPrice: z.number(),
	totalSupply: z.number(),
	circulatingSupply: z.number(),
	marketCapitalizationUsd: z.number()
});

// Network
export const NetworkStatsSchema = z.object({
  totalCheckpoints: z.string(),
  totalTransactions: z.string(),
  referenceGasPrice: z.string(),
	marketData: marketData.nullable()
})
export type NetworkStats = z.infer<typeof NetworkStatsSchema>

export const NetworkThroughputSchema = z.object({
  ptbThroughput: z.number(),
})
export type NetworkThroughput = z.infer<typeof NetworkThroughputSchema>

export const NetworkAverageGasFeeSchema = z.object({
  averageGasFee: z.number(),
})
export type NetworkAverageGasFee = z.infer<typeof NetworkAverageGasFeeSchema>

export const NetworkDailyActiveAccountsSchema = z.array(
  z.object({
    DAY: z.string(),
    ACTIVE_ACCOUNTS: z.number(),
  })
)
export type NetworkDailyActiveAccounts = z.infer<typeof NetworkDailyActiveAccountsSchema>

export const NetworkDailyTransactionBlocksSchema = z.array(
  z.object({
    DAY: z.string(),
    PTB_COUNT: z.number(),
  })
)
export type NetworkDailyTransactionBlocks = z.infer<typeof NetworkDailyTransactionBlocksSchema>

export const NetworkDailyTransactionsSchema = z.array(
  z.object({
    DAY: z.string(),
    TRANSACTION_COUNT: z.number(),
  })
)
export type NetworkDailyTransactions = z.infer<typeof NetworkDailyTransactionsSchema>


// Checkpoints
export const CheckpointSchema = z.object({
  digest: z.string(),
  sequenceNumber: z.string(),
  timestampMS: z.string()
})
export type Checkpoint = z.infer<typeof CheckpointSchema>

export const CheckpointPageSchema = z.object({
	data: z.array(CheckpointSchema),
	hasNextPage: z.boolean(),
	nextCursor: z.string()
});
export type CheckpointPage = z.infer<typeof CheckpointPageSchema>;

export const CheckpointDetailsSchema = z.object({
	digest: z.string(),
  sequenceNumber: z.string(),
  timestampMS: z.string(),
	previousDigest: z.string(),
	gasUsage: z.object({
		computationCost: z.string(),
		storageCost: z.string(),
		storageRebate: z.string(),
		nonRefundableStorageFee: z.string()
	}),
	numberOfTransactions: z.number()
})
export type CheckpointDetails = z.infer<typeof CheckpointDetailsSchema>;

export const CheckpointTransactionBlocksSchema = z.array(z.object({
	digest: z.string(),
  checkpoint: z.string(),
  timestampMS: z.string(),
}));
export type CheckpointTransactionBlocks = z.infer<typeof CheckpointTransactionBlocksSchema>;


// Transaction Blocks
export const TransactionBlockSchema = z.object({
	digest: z.string(),
	checkpoint: z.string(), 
  timestampMS: z.string(),
});
export type TransactionBlock = z.infer<typeof TransactionBlockSchema>

export const TransactionBlockDetailsSchema = z.object({
	digest: z.string(),
	sequenceNumber: z.string(), 
  timestampMS: z.string(),
	sender: z.string(),
	gasUsage: z.object({
		computationCost: z.string(),
		storageCost: z.string(),
		storageRebate: z.string(),
		nonRefundableStorageFee: z.string()
	}),
	status: z.string()
})
export type TransactionBlockDetails = z.infer<typeof TransactionBlockDetailsSchema>;

export const TransactionBlockRecentsSchema = z.object({
	data: z.array(TransactionBlockSchema),
	cursor: z.object({
		sequenceNumber: z.string(),
		txOffset: z.number()
	}).nullable()
})
export type TransactionBlockRecents = z.infer<typeof TransactionBlockRecentsSchema>


// Packages
export const PackageDetailsSchema = z.object({
	id: z.string(),
	version: z.string(), 
  publisher: z.string(),
	createdAtMS: z.string()
});
export type PackageDetails = z.infer<typeof PackageDetailsSchema>

export const PackageTransactionBlocksSchema = z.object({
	data: z.array(TransactionBlockSchema),
	hasNextPage: z.boolean(),
	cursor: z.string() 
});
export type PackageTransactionBlocks = z.infer<typeof PackageTransactionBlocksSchema>


// Accounts
export const AddressDetailsSchema = z.object({
  address: z.string(),
  suiBalance: z.object({
		coinType: z.string(),
		totalBalance: z.string()
	})
})
export type AddressDetails = z.infer<typeof AddressDetailsSchema>

export const AddressBalancesSchema = z.array(z.object({
  coinType: z.string(),
  totalBalance: z.string(),
}));
export type AddressBalances = z.infer<typeof AddressBalancesSchema>

export const AddressTransactionBlocksSchema = z.object({
  data: z.array(TransactionBlockSchema),
	hasNextPage: z.boolean(),
	cursor: z.string() 
});
export type AddressTransactionBlocks = z.infer<typeof AddressTransactionBlocksSchema>


// Coins
export const CoinDetailsSchema = z.object({
	symbol: z.string(),
	name: z.string(),
	packageId: z.string(),
	publisherAddress: z.string(),
	marketData: marketData.nullable()
});
export type CoinDetails = z.infer<typeof CoinDetailsSchema>;

// Objects
const ownerSchema = z.union([
  z.object({
    AddressOwner: z.string()
  }),
  z.object({
    ObjectOwner: z.string()
  }),
  z.object({
    Shared: z.object({
      initial_shared_version: z.number()
    })
  }),
  z.literal('Immutable')
]);


export const ObjectDetailsSchema = z.object({
  objectId: z.string(),
  version: z.string(),
  owner: ownerSchema,
  digest: z.string(),
  storageRebate: z.string(),
})
export type ObjectDetails = z.infer<typeof ObjectDetailsSchema>


// Search
export const SearchResponseSchema = z.object({
  type: z.string().nullable(),
  redirectId: z.string().nullable()
})
export type SearchResponse = z.infer<typeof SearchResponseSchema>



// Paginated Response Schema
export const PaginatedResponseSchema = <T extends z.ZodType>(schema: T) =>
  z.object({
    data: z.array(schema),
    hasNextPage: z.boolean(),
    nextCursor: z.string().optional(),
  })

// API Client Class
export class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      })

      const data = await response.json()

      if (!response.ok) {
        const error = ApiErrorSchema.parse(data)
        throw new Error(error.message)
      }

      return data as T
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('An unknown error occurred')
    }
  }

  // Network 
  async getNetworkStats(): Promise<NetworkStats> {
    const data = await this.request<NetworkStats>('/sui/v1/network/stats')
    return NetworkStatsSchema.parse(data)
  }
  async getNetworkThroughput(): Promise<NetworkThroughput> {
    const data = await this.request<NetworkThroughput>('/sui/v1/network/throughput')
    return NetworkThroughputSchema.parse(data)
  }
  async getNetworkAverageGasFee(): Promise<NetworkAverageGasFee> {
    const data = await this.request<NetworkAverageGasFee>('/sui/v1/network/average-gas-fee')
    return NetworkAverageGasFeeSchema.parse(data)
  }
  async getNetworkDailyActiveAccounts(): Promise<NetworkDailyActiveAccounts> {
    const data = await this.request<NetworkDailyActiveAccounts>(
      '/sui/v1/network/daily-active-accounts'
    )
    return NetworkDailyActiveAccountsSchema.parse(data)
  }
  async getNetworkDailyTransactionBlocks(): Promise<NetworkDailyTransactionBlocks> {
    const data = await this.request<NetworkDailyTransactionBlocks>(
      '/sui/v1/network/daily-transaction-blocks'
    )
    return NetworkDailyTransactionBlocksSchema.parse(data)
  }
  async getNetworkDailyTransactions(): Promise<NetworkDailyTransactions> {
    const data = await this.request<NetworkDailyTransactions>(
      '/sui/v1/network/daily-transactions'
    )
    return NetworkDailyTransactionsSchema.parse(data)
  }

	
	// Checkpoints
	async getCheckpointRecents(limit?: number, cursor?: string|null): Promise<CheckpointPage> {
    let url = `/sui/v1/checkpoints/recent`;
		if (limit || cursor) url = url + '?';
		url = url + `${limit ? `limit=${limit}&` : ''}` + `${cursor ? `cursor=${cursor}` : ''}`;
		const data = await this.request<CheckpointPage>(url)
    return CheckpointPageSchema.parse(data)
  }
	async getCheckpoint(id: string): Promise<CheckpointDetails> {
    const data = await this.request<CheckpointDetails>(`/sui/v1/checkpoints/${id}`)
    return CheckpointDetailsSchema.parse(data)
  }
	async getCheckpointTransactionBlocks(id: string, limit?: number, offset?: number): Promise<CheckpointTransactionBlocks> {
		let baseUrl = `/sui/v1/checkpoints/${id}/ptbs`;
		if (limit || offset) baseUrl = baseUrl + '?';
		baseUrl = baseUrl + `${limit ? `limit=${limit}&` : ''}` + `${offset ? `offset=${offset}` : ''}`;

		const data = await this.request<CheckpointTransactionBlocks>(baseUrl);
    return CheckpointTransactionBlocksSchema.parse(data)
  }

	
	// Transaction Blocks
	async getTransactionBlockRecents(limit?: number, sequenceNumber?: string | null, txOffset?: number | null): Promise<TransactionBlockRecents> {
		let url = `/sui/v1/ptbs/recent`;
		if (limit || sequenceNumber || txOffset) url = url + '?';
		url = url + `${limit ? `limit=${limit}&` : ''}` 
			+ `${sequenceNumber !== null ? `sequenceNumber=${sequenceNumber}&` : ''}`
			+ `${txOffset !== null ? `txOffset=${txOffset}` : ''}`;

    const data = await this.request<TransactionBlockRecents>(url)
    return TransactionBlockRecentsSchema.parse(data)
  }
	async getRecentTransactions(limit: number = 20) {
    const params = new URLSearchParams({ limit: limit.toString() })

    const data = await this.request(
      `/sui/v1/ptbs/recent?${params.toString()}`
    )
    return PaginatedResponseSchema(TransactionBlockSchema).parse(data)
  }
  async getTransactionBlockDetails(digest: string): Promise<TransactionBlockDetails> {
    const data = await this.request<TransactionBlockDetails>(`/sui/v1/ptbs/${digest}`)
    return TransactionBlockDetailsSchema.parse(data)
  }


	// Packages
	async getPackageDetails(id: string): Promise<PackageDetails> {
		const data = await this.request<PackageDetails>(`/sui/v1/packages/${id}`)
    return PackageDetailsSchema.parse(data)
	}
	async getPackageTransactionBlocks(id: string, limit?: number, cursor?: string | null): Promise<PackageTransactionBlocks> {		
		let url = `/sui/v1/packages/${id}/ptbs`;
		if (limit || cursor) url = url + '?';
		url = url + `${limit ? `limit=${limit}&` : ''}` + `${cursor ? `cursor=${cursor}` : ''}`;

		const data = await this.request<PackageTransactionBlocks>(url);
    return PackageTransactionBlocksSchema.parse(data);
	}

	// Accounts
	async getAddressDetails(id: string): Promise<AddressDetails> {
		const data = await this.request<AddressDetails>(`/sui/v1/accounts/${id}`)
    return AddressDetailsSchema.parse(data)
	}
	async getAddressBalances(id: string): Promise<AddressBalances> {
		const data = await this.request<AddressBalances>(`/sui/v1/accounts/${id}/balances`)
    return AddressBalancesSchema.parse(data)
	}
	async getAddressFromTransactions(id: string, limit?: number, cursor?: string | null): Promise<AddressTransactionBlocks> {
		let url = `/sui/v1/accounts/${id}/from-activity`;
		if (limit || cursor) url = url + '?';
		url = url + `${limit ? `limit=${limit}&` : ''}` + `${cursor ? `cursor=${cursor}` : ''}`;

		const data = await this.request<AddressTransactionBlocks>(url);
    return AddressTransactionBlocksSchema.parse(data)
	}
	async getAddressToTransactions(id: string, limit?: number, cursor?: string | null): Promise<AddressTransactionBlocks> {
		let url = `/sui/v1/accounts/${id}/to-activity`;
		if (limit || cursor) url = url + '?';
		url = url + `${limit ? `limit=${limit}&` : ''}` + `${cursor ? `cursor=${cursor}` : ''}`;
		
		const data = await this.request<AddressTransactionBlocks>(url)
    return AddressTransactionBlocksSchema.parse(data)
	}


	// Coins
	async getCoinDetails(id: string): Promise<CoinDetails> {
    const data = await this.request<CoinDetails>(`/sui/v1/coins/${id}`)
    return CoinDetailsSchema.parse(data)
  }


	// Objects
  async getObjectDetails(id: string): Promise<ObjectDetails> {
    const data = await this.request<ObjectDetails>(`/sui/v1/objects/${id}`)
    return ObjectDetailsSchema.parse(data)
  }


	// Search
	async getSearchResponse(id: string): Promise<SearchResponse> {
		const data = await this.request<SearchResponse>(`/sui/v1/search?id=${id}`)
    return SearchResponseSchema.parse(data)
  }
	
}

// Create and export default instance
export const api = new ApiClient()